// toast.tsx — VoiceSpectrum SHARED toast system (canonical) · v12.1.17.0
// ONE implementation, copied identically into T2/T3/T4 src/lib/ (there is no shared frontend package).
// Locked design (SPEC_shared__notifications_v1_0 + toast_system_v1_1.html): bottom-center region,
// variants success/error/warning/info/loading with distinct icons, progress-bar countdown, hover-pause,
// loading→resolve for async actions. Self-contained: injects its own styles (literal colors) so every
// surface renders identically regardless of local tokens.
//
// Imperative API (call from anywhere, incl. mutation handlers):
//   import { toast } from '../lib/toast';
//   toast('success', 'Provider saved', 'OpenAI Text Embedding is now active.');
//   const h = toast('loading', 'Checking connections…'); h.resolve('success', 'Connections healthy');
// Mount <ToastHost /> once at the app root (or wrap the app in <ToastProvider>).
//
// NOTE: per-variant durations below are the SEED defaults. NT1 (v12.1.17.0) made them platform config —
// each app's main.tsx calls setToastDurations() at load from GET api/notifications/toast-durations (T4 › Platform › Notifications).
import { ReactNode, useEffect, useRef, useState } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';
export interface ToastOptions { durationMs?: number }
// Back-compat with T3's earlier toast API (useToast().showToast(args)).
export interface ShowToastArgs { kind: ToastType; message: string; durationMs?: number }
export interface ToastHandle { id: number; close: () => void; resolve: (type: ToastType, title: string, message?: string) => ToastHandle }

interface ToastRec { id: number; type: ToastType; title: string; message?: string; durationMs: number }

// Seed durations (ms). Loading has no timer. Override via setToastDurations() when T4 config lands (NT1).
let DURATIONS: Record<Exclude<ToastType, 'loading'>, number> = { success: 4000, info: 4000, warning: 5000, error: 7000 };
export function setToastDurations(cfg: Partial<Record<Exclude<ToastType, 'loading'>, number>>) { DURATIONS = { ...DURATIONS, ...cfg }; }

let _items: ToastRec[] = [];
let _nextId = 1;
const _listeners = new Set<(items: ToastRec[]) => void>();
function emit() { const snapshot = [..._items]; _listeners.forEach((l) => l(snapshot)); }
function remove(id: number) { _items = _items.filter((t) => t.id !== id); emit(); }

/** Fire a toast. Returns a handle: close() to dismiss, resolve() to swap a loading toast for a result. */
export function toast(type: ToastType, title: string, message?: string, opts?: ToastOptions): ToastHandle {
  const id = _nextId++;
  const durationMs = opts?.durationMs ?? (type === 'loading' ? 0 : DURATIONS[type]);
  _items = [..._items, { id, type, title, message, durationMs }];
  emit();
  const handle: ToastHandle = {
    id,
    close: () => remove(id),
    resolve: (t, ti, m) => { remove(id); return toast(t, ti, m); },
  };
  return handle;
}

const ICONS: Record<ToastType, ReactNode> = {
  success: <path d="M20 6 9 17l-5-5" />,
  error: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
  warning: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></>,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" /></>,
  loading: <path className="vt-spin" d="M21 12a9 9 0 1 1-6.2-8.6" />,
};

function ToastItem({ rec, onClose }: { rec: ToastRec; onClose: (id: number) => void }) {
  const [leaving, setLeaving] = useState(false);
  const progRef = useRef<HTMLSpanElement | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const dismiss = () => { setLeaving(true); window.setTimeout(() => onClose(rec.id), 200); };

  useEffect(() => {
    if (rec.type === 'loading' || rec.durationMs <= 0) return;
    const prog = progRef.current;
    if (prog) { prog.style.transition = `width ${rec.durationMs}ms linear`; prog.style.width = '100%'; requestAnimationFrame(() => { if (prog) prog.style.width = '0%'; }); }
    timerRef.current = window.setTimeout(dismiss, rec.durationMs);
    return () => window.clearTimeout(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onEnter = () => {
    if (rec.type === 'loading') return;
    window.clearTimeout(timerRef.current);
    const prog = progRef.current;
    if (prog) { const w = getComputedStyle(prog).width; prog.style.transition = 'none'; prog.style.width = w; }
  };
  const onLeave = () => {
    if (rec.type === 'loading') return;
    const prog = progRef.current;
    if (prog) { prog.style.transition = 'width 1400ms linear'; prog.style.width = '0%'; }
    timerRef.current = window.setTimeout(dismiss, 1400);
  };

  return (
    <div className={`vt-toast vt-${rec.type}${leaving ? ' vt-out' : ''}`} role={rec.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <svg className="vt-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{ICONS[rec.type]}</svg>
      <div className="vt-tbody"><div className="vt-title">{rec.title}</div>{rec.message && <div className="vt-msg">{rec.message}</div>}</div>
      <button className="vt-close" onClick={dismiss} aria-label="Dismiss"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
      {rec.type !== 'loading' && <span className="vt-prog" ref={progRef} />}
    </div>
  );
}

/** Mount ONCE at the app root. Renders the bottom-center toast region. */
export function ToastHost() {
  const [items, setItems] = useState<ToastRec[]>([]);
  useEffect(() => { _listeners.add(setItems); setItems([..._items]); return () => { _listeners.delete(setItems); }; }, []);
  useEffect(() => { injectStyles(); }, []);
  return (
    <div className="vt-region" aria-live="polite">
      {items.map((t) => <ToastItem key={t.id} rec={t} onClose={remove} />)}
    </div>
  );
}

/** Optional wrapper (back-compat with T3's usage): renders children + the host. */
export function ToastProvider({ children }: { children: ReactNode }) { return <>{children}<ToastHost /></>; }

/** Back-compat hook (T3 used useToast().showToast({kind,message})). Maps onto the new API. */
export function useToast() {
  return { showToast: (args: { kind: ToastType; message: string; durationMs?: number }) => toast(args.kind, args.message, undefined, { durationMs: args.durationMs }) };
}

let _stylesInjected = false;
function injectStyles() {
  if (_stylesInjected || typeof document === 'undefined') return;
  _stylesInjected = true;
  const css = `
.vt-region{position:fixed;left:50%;bottom:0;transform:translateX(-50%);z-index:99999;display:flex;flex-direction:column;align-items:center;gap:10px;padding:0 16px calc(24px + env(safe-area-inset-bottom));pointer-events:none;width:100%;max-width:440px}
.vt-toast{pointer-events:auto;position:relative;overflow:hidden;display:flex;align-items:flex-start;gap:11px;width:100%;background:#fff;border:1px solid #e2e8f0;border-left:4px solid #64748b;border-radius:12px;padding:13px 14px;box-shadow:0 10px 30px rgba(15,23,42,.14),0 2px 8px rgba(15,23,42,.08);animation:vtIn .28s cubic-bezier(.2,.9,.3,1)}
.vt-toast.vt-out{animation:vtOut .2s ease forwards}
@keyframes vtIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
@keyframes vtOut{to{opacity:0;transform:translateY(10px) scale(.98)}}
.vt-ic{flex-shrink:0;width:20px;height:20px;margin-top:1px}
.vt-tbody{flex:1;min-width:0}
.vt-title{font-size:13.5px;font-weight:700;color:#0f172a}
.vt-msg{font-size:12.5px;color:#475569;margin-top:2px;line-height:1.45}
.vt-close{flex-shrink:0;width:22px;height:22px;border:0;background:transparent;color:#94a3b8;padding:0;display:grid;place-items:center;border-radius:6px;cursor:pointer}
.vt-close:hover{background:#f7f9fc;color:#475569}
.vt-close svg{width:14px;height:14px}
.vt-success{border-left-color:#059669}.vt-success .vt-ic{color:#059669}
.vt-error{border-left-color:#dc2626}.vt-error .vt-ic{color:#dc2626}
.vt-warning{border-left-color:#b45309}.vt-warning .vt-ic{color:#b45309}
.vt-info{border-left-color:#2563eb}.vt-info .vt-ic{color:#2563eb}
.vt-loading .vt-ic{color:#4f46e5}
.vt-spin{animation:vtSpin 1s linear infinite;transform-origin:center}
@keyframes vtSpin{to{transform:rotate(360deg)}}
.vt-prog{position:absolute;left:0;bottom:0;height:2px;background:currentColor;opacity:.22}
.vt-success .vt-prog{color:#059669}.vt-error .vt-prog{color:#dc2626}.vt-warning .vt-prog{color:#b45309}.vt-info .vt-prog{color:#2563eb}
@media (prefers-reduced-motion: reduce){.vt-toast{animation:none}.vt-toast.vt-out{animation:none}.vt-spin{animation:none}.vt-prog{transition:none!important}}
`;
  const style = document.createElement('style');
  style.id = 'vs-toast-styles';
  style.textContent = css;
  document.head.appendChild(style);
}

// VoiceSpectrum shared UI · toast system · v12.1.17.0 · 2026-07-01
